import {
    apiMediaUrl,
    apiPortalUrl,
    apiUrl,
    AppConstants,
    COMMON_PROTOCOL,
    CurrentcyPositions,
    DATE_FORMAT_DISPLAY,
    DATE_SHORT_MONTH_FORMAT,
    daysOfWeekTimeWork,
    DEFAULT_FORMAT,
    DEFAULT_TABLE_ITEM_SIZE,
    DONE_TYPE_VALUE_REQUIRES_APPROVAL,
    THEMES,
    TIME_DELETE_ARCHIVE_ORDER_DEFAULT,
    TIME_FORMAT_DISPLAY,
} from '@constants';
import { timeZoneOptions } from '@constants/timezone';
import { commonMessage } from '@locales/intl';
import { getCacheX_tenant } from '@services/userService';
import store from '@store';
import { notification } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import qs from 'query-string';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

export const convertGlobImportToObject = (modules) =>
    modules
        .filter((module) => !!module.default)
        .reduce(
            (rs, cur) => ({
                ...rs,
                [cur.default.name]: cur.default,
            }),
            {},
        );

export const convertGlobImportToArray = (modules) =>
    modules.filter((module) => !!module.default).map((module) => module.default);

export const destructCamelCaseString = (string) => {
    const arrString = [...string];
    const newArrString = [];
    arrString.forEach((char, index) => {
        if (char.charCodeAt(0) > 90) {
            newArrString.push(char);
        } else {
            index && newArrString.push('-');
            newArrString.push(char.toLowerCase());
        }
    });
    return newArrString.join('');
};

export const convertUtcToLocalTime = (utcTime, inputFormat = DATE_FORMAT_DISPLAY, format = DATE_FORMAT_DISPLAY) => {
    try {
        if (utcTime) return dayjs.utc(utcTime, inputFormat).local().format(format);
        return '';
    } catch (err) {
        return '';
    }
};

export const convertLocalTimeToUtc = (localTime, inputFormat = DEFAULT_FORMAT, format = DEFAULT_FORMAT) => {
    try {
        if (localTime) return dayjs(localTime, inputFormat).utc().format(format);
        return '';
    } catch (err) {
        return '';
    }
};

export const convertToUtcStartOfDay = (localDate, inputFormat = DATE_FORMAT_DISPLAY, format = DEFAULT_FORMAT) => {
    try {
        if (localDate) return dayjs(localDate, inputFormat).startOf('day').utc().format(format);
        return '';
    } catch (err) {
        return '';
    }
};

export const convertToUtcEndOfDay = (localDate, inputFormat = DATE_FORMAT_DISPLAY, format = DEFAULT_FORMAT) => {
    try {
        if (localDate) return dayjs(localDate, inputFormat).endOf('day').utc().format(format);
        return '';
    } catch (err) {
        return '';
    }
};

export function convertUtcToIso(date) {
    return dayjs(convertUtcToLocalTime(date, DEFAULT_FORMAT, DEFAULT_FORMAT), DEFAULT_FORMAT);
}

export const getBrowserTheme = () => {
    const isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

    return isDark ? THEMES.DARK : THEMES.LIGHT;
};

export const makeURL = (baseURL, params, pathParams) => {
    for (let key of Object.keys(pathParams || {})) {
        const keyCompare = `:${key}`;
        if (baseURL.indexOf(keyCompare) !== -1) {
            baseURL = baseURL.replace(keyCompare, pathParams[key]);
        }
    }

    if (params) {
        baseURL = baseURL + '?' + qs.stringify(params);
    }

    return baseURL;
};

export const parseURL = (url) => {
    try {
        return new URL(url);
    } catch (error) {
        return '';
    }
};

export const getYTEmbedLinkFromYTWatchLink = (watchLink) => {
    if (!watchLink) {
        return '';
    }

    const { v } = qs.parse(parseURL(watchLink).search);
    return v ? `https://www.youtube.com/embed/${v}?autoplay=1&mute=1` : watchLink;
};

export const getYoutubeVideoID = (url) => {
    let pattern = /(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/gm;
    return pattern.exec(url)?.[3];
};

export const formatNumber = (value, setting) => {
    if (value) {
        const decimalPosition = value.toString().indexOf('.');
        if (decimalPosition > 0) {
            const intVal = value.toString().substring(0, decimalPosition);
            const decimalVal = value.toString().substring(decimalPosition + 1);
            return `${intVal.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decimalVal}`;
        }
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    } else if (value === 0) return 0;
    return '';
};

export const formatDateString = (dateString, formatDate = DATE_SHORT_MONTH_FORMAT) => {
    return dayjs(dateString).format(formatDate);
};

export const removeAccents = (str) => {
    if (str)
        return str
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/đ/g, 'd')
            .replace(/Đ/g, 'D');
    return str;
};

export const validateUsernameForm = (rule, username) => {
    return /^[a-z0-9_]+$/.exec(username)
        ? Promise.resolve()
        : Promise.reject('Username chỉ bao gồm các ký tự a-z, 0-9, _');
};

export const getBase64 = (img, callback) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result));
    reader.readAsDataURL(img);
};

export function ensureArray(value) {
    if (value === null || value === undefined) {
        return [];
    }

    if (Array.isArray(value)) {
        return value;
    }

    return [value];
}

export const removePathParams = (paths) => {
    return ensureArray(paths).map((path) => {
        if (typeof path !== 'string') return path;
        return path.replaceAll(/\/:[a-zA-Z]+/g, '');
    });
};

export const validatePermission = (
    requiredPermissions = [],
    userPermissions = [],
    requiredKind,
    excludeKind = [],
    userKind,
    profile,
    path,
    separate,
) => {
    const tenant = getCacheX_tenant();
    if (ensureArray(excludeKind).length > 0) {
        if (ensureArray(excludeKind).some((kind) => kind == userKind)) return false;
    }
    if (requiredKind) {
        if (requiredKind !== userKind) return false;
    }
    if (!requiredPermissions || requiredPermissions?.length == 0) return true;

    const permissions = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
    if (permissions.every((code) => userPermissions.includes(code))) {
        return true;
    }

    let permissionsSavePage = [];
    if (separate && requiredPermissions.length > 0) {
        permissionsSavePage.push(path?.type === 'create' ? requiredPermissions[0] : requiredPermissions[1]);
    } else {
        permissionsSavePage = requiredPermissions;
    }
    if (tenant) {
        return removePathParams(permissionsSavePage).every((item) => {
            let processedItem = item;
            if (apiPortalUrl && item.startsWith(apiPortalUrl)) {
                processedItem = item.replace(apiPortalUrl, '/');
            }
            else if (apiUrl && item.startsWith(apiUrl)) {
                processedItem = item.replace(apiUrl, '/');
            } else if (apiMediaUrl && item.startsWith(apiMediaUrl)) {
                processedItem = item.replace(apiMediaUrl, '/');
            }
            const isMatched = userPermissions?.includes(processedItem);

            return isMatched;
        });
    } else {
        return removePathParams(permissionsSavePage).every((item) => {
            let processedItem = item;
            if (apiUrl && item?.startsWith(apiUrl)) {
                processedItem = item.replace(apiUrl, '/');
            } else if (apiMediaUrl && item?.startsWith(apiMediaUrl)) {
                processedItem = item.replace(apiMediaUrl, '/');
            }
            return userPermissions?.includes(processedItem);
        });
    }
};

export function generatePassword(options) {
    const { length, numbers, uppercase, lowercase, symbols, strict } = options;

    const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
    const numberChars = '0123456789';
    const symbolChars = '!@#$%^&*()_+[]{}|;:,.<>?';

    let validChars = '';

    if (uppercase) {
        validChars += uppercaseChars;
    }
    if (lowercase) {
        validChars += lowercaseChars;
    }
    if (numbers) {
        validChars += numberChars;
    }
    if (symbols) {
        validChars += symbolChars;
    }

    if (validChars.length === 0) {
        throw new Error('At least one character type should be selected.');
    }

    let password = '';

    for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * validChars.length);
        password += validChars.charAt(randomIndex);
    }

    if (strict) {
        // Ensure at least one character of each type is present
        if (uppercase && !/[A-Z]/.test(password)) {
            return generatePassword(options);
        }
        if (lowercase && !/[a-z]/.test(password)) {
            return generatePassword(options);
        }
        if (numbers && !/\d/.test(password)) {
            return generatePassword(options);
        }
        if (symbols && !/[!@#$%^&*()_+[\]{}|;:,.<>?]/.test(password)) {
            return generatePassword(options);
        }
    }

    return password;
}
export function copyToClipboard(text, translate) {
    try {
        const textField = document.createElement('textarea');
        textField.innerText = text;
        document.body.appendChild(textField);
        textField.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textField);

        if (successful) {
            notification.success({
                message: translate ? translate.formatMessage(commonMessage.success) : 'Success',
                description: translate ? translate.formatMessage(commonMessage.copySuccess) : 'Copy success!',
            });
        } else {
            notification.error({
                message: translate ? translate.formatMessage(commonMessage.failed) : 'Failed',
                description: translate ? translate.formatMessage(commonMessage.copySuccess) : 'Copy failed!',
            });
        }
    } catch (err) {
        notification.error({
            message: translate ? translate.formatMessage(commonMessage.failed) : 'Failed',
            description: translate ? translate.formatMessage(commonMessage.copySuccess) : 'Copy failed!',
        });
    }
}

export const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export const handleRemoveImage = (setImageUrl, form) => {
    setImageUrl(null);
    form.setFieldsValue({ imageUrl: null });
};

export const parseJson = (json) => {
    let result = null;
    if (json) {
        try {
            result = JSON.parse(json);
        } catch (err) {
            //eslint-disable-next-line
            console.error(err);
        }
    }
    return result;
};

export const calcCoefficient = (value, coefficient = 1) => {
    return +((value || 0) * coefficient).toFixed(2);
};

export const calcBeilagePriceCoefficient = (beilages, coefficient = 1) => {
    return beilages.map((bei) => ({
        ...bei,
        food_datas: (bei.food_datas || []).map(({ price, ...rest }) => ({
            price: calcCoefficient(price, coefficient),
            ...rest,
        })),
    }));
};

export const calcFoodPriceCoefficient = (price, coefficient = 1) => {
    return {
        qr_code: {
            in_price: calcCoefficient(price.qr_code?.in_price, coefficient),
            in_sale_off_percent: price.qr_code?.in_sale_off_percent,
            out_price: calcCoefficient(price.qr_code?.out_price, coefficient),
            out_sale_off_percent: price.qr_code?.out_sale_off_percent,
        },
        pickup: {
            price: calcCoefficient(price.pickup?.price, coefficient),
            sale_off_percent: price.pickup?.sale_off_percent,
        },
        deliver: {
            price: calcCoefficient(price.deliver?.price, coefficient),
            sale_off_percent: price.deliver?.sale_off_percent,
        },
    };
};

export const formatMoney = (value, setting) => {
    if ((value || value === 0) && !isNaN(value)) {
        const groupSeparator = setting.group_separator || '.';
        const decimalSeparator = setting.decimal_separator || ',';
        const currentcy = setting.currentcy || '€';
        const currentcyPosition = setting.currentcy_position || CurrentcyPositions.BACK;
        value = (+value).toFixed(2);
        const decimalPosition = value.toString().indexOf('.');
        if (decimalPosition > 0) {
            const intVal = value.toString().substring(0, decimalPosition);
            const decimalVal = value.toString().substring(decimalPosition + 1);
            value = `${intVal.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator)}${decimalSeparator}${decimalVal}`;
        } else {
            value = value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);
        }
        if (currentcyPosition === CurrentcyPositions.FRONT) {
            return `${currentcy} ${value}`;
        } else {
            return `${value} ${currentcy}`;
        }
    }
    return '';
};

export const mapBeilageTemplateToBeilage = (beilageTemplate) => {
    return (beilageTemplate.beilages || []).map(({ food_datas, ...rest }) => ({
        ...rest,
        food_datas: (food_datas || []).map(({ price, ...rest }) => ({
            ...rest,
            price: calcCoefficient(price, 100),
        })),
    }));
};

export const parseConnectionString = (url) => {
    const cleanString = url.substring(COMMON_PROTOCOL.length, url.indexOf('?'));
    const host = cleanString.substring(0, cleanString.indexOf(':'));
    const port = cleanString.substring(cleanString.indexOf(':') + 1, cleanString.indexOf('/'));
    const database = cleanString.substring(cleanString.indexOf('/') + 1, cleanString.length);
    return { host, port, database };
};


export function generateSecret(length = 64) {
    const charset =
        'abcdefghijklmnopqrstuvwxyz' + 'ABCDEFGHIJKLMNOPQRSTUVWXYZ' + '0123456789' + '!@#$%^&*()_+[]{}|;:,.<>?/';

    let result = '';
    const randomValues = new Uint32Array(length);
    window.crypto.getRandomValues(randomValues);

    for (let i = 0; i < length; i++) {
        const index = randomValues[i] % charset.length;
        result += charset[index];
    }

    return result;
}

export const orderNumber = (pagination, index) => {
    const page = pagination?.current ? pagination.current - 1 : 1;
    return page * DEFAULT_TABLE_ITEM_SIZE + (index + 1);
};

export const getDefaultTimeWork = () => {
    const timeWorkData = { dayOffs: [] };
    daysOfWeekTimeWork.forEach((day) => {
        timeWorkData[day.value] = [
            { from: dayjs('00:00', TIME_FORMAT_DISPLAY), to: dayjs('00:00', TIME_FORMAT_DISPLAY) },
            { from: dayjs('00:00', TIME_FORMAT_DISPLAY), to: dayjs('00:00', TIME_FORMAT_DISPLAY) },
        ];
    });
    return timeWorkData;
};

export const parseSettings = (raw) => {
    try {
        return raw ? JSON.parse(raw) : {};
    } catch (e) {
        return {};
    }
};

export const checkImageValid = (url) => {
    return new Promise((resolve) => {
        if (!url) return resolve(false);

        const img = new window.Image();

        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);

        img.src = url;
    });
};

export const getFullImageUrl = (url, rootUrl = AppConstants.contentRootUrl) => {
    if (!url) return null;

    const cleanUrl = url.trim();

    const isFullUrl = /^(https?:\/\/|data:image\/)/i.test(cleanUrl);

    return isFullUrl ? cleanUrl : `${rootUrl}${cleanUrl}`;
};

export const convertUrlToBase64 = async (url, rootUrl) => {
    const fullUrl = getFullImageUrl(url, rootUrl);
    const response = await fetch(fullUrl);
    const blob = await response.blob();
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
    });
};

export const getDefaultSettings = () => {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tzEntry = timeZoneOptions.find((o) => o.value === browserTz);
    const timezone = {
        name: tzEntry ? browserTz : 'Asia/Ho_Chi_Minh',
        offset: tzEntry?.offset || '+7:00',
    };
    return JSON.stringify({
        timezone,
        currentcy: '$',
        currentcy_position: 2,
        date_time_format: 'dd.MM.yyyy',
        decimal_separator: ',',
        decimal_space: '2',
        group_separator: '.',
    });
};

export const getDefaultPiServerSettings = () => JSON.stringify({
    kitplay: {
        time_delete_archive_order: TIME_DELETE_ARCHIVE_ORDER_DEFAULT,
        done_type: DONE_TYPE_VALUE_REQUIRES_APPROVAL,
        push_to_mpos_inhouse: true,
        push_to_mpos_imbiss: true,
        push_to_mpos_takeaway: true,
        push_to_mpos_delivery: true,
    },
});

export const getDefaultPrinterSetting = () => JSON.stringify({
    late_time: 1,
    group_take_away: true,
    group_delivery: false,
    food_break_count: 2,
    win_print: false,
});
